import { useSyncExternalStore, useCallback } from 'react'

/**
 * Whether the left sidebar is collapsed to an icon rail. Persisted in
 * localStorage and shared across components (Layout + Sidebar) via an external
 * store so the main-content padding and the sidebar stay in sync.
 */
const KEY = 'nav-collapsed'
const listeners = new Set<() => void>()
let cache: boolean | null = null

function read(): boolean {
  if (cache === null) cache = localStorage.getItem(KEY) === 'true'
  return cache
}

function write(next: boolean) {
  cache = next
  try {
    localStorage.setItem(KEY, String(next))
  } catch {
    /* ignore quota/availability errors */
  }
  listeners.forEach((l) => l())
}

function subscribe(cb: () => void) {
  listeners.add(cb)
  return () => {
    listeners.delete(cb)
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key === KEY) {
      cache = null
      listeners.forEach((l) => l())
    }
  })
}

export function useSidebarCollapsed(): [boolean, (value?: boolean) => void] {
  const collapsed = useSyncExternalStore(subscribe, read, () => false)
  const setCollapsed = useCallback((value?: boolean) => {
    write(typeof value === 'boolean' ? value : !read())
  }, [])
  return [collapsed, setCollapsed]
}
